import React from 'react'
import { useShallow } from 'zustand/react/shallow'
import { RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import useStore, { isComparing } from '@/stores/useStore'
import { arePlansEquivalent } from '@/lib/planSettings'
import { isProfitSharingEnabled, referencedClassesForPlan, selectProfitSharingDesign } from '@/lib/planDesign'
import { countClassMembers, deriveAllocationClasses } from '@/lib/allocationClasses'
import ProfitSharingSection from '@/components/plan/ProfitSharingSection'
import CashBalanceStructuredCard from '@/components/plan/CashBalanceStructuredCard'
import MatchCard, { createMatchGroup } from '@/components/plan/MatchCard'
import NecCard from '@/components/plan/NecCard'
import ProfitSharingCard from '@/components/plan/ProfitSharingCard'
import AfterTaxCard from '@/components/plan/AfterTaxCard'
import EligibilityCard from '@/components/plan/EligibilityCard'
import PlanYearCard from '@/components/plan/PlanYearCard'
import TerminationCard from '@/components/plan/TerminationCard'
import VestingCard from '@/components/plan/VestingCard'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { isDemoCensusAllowed } from '@/lib/demoEntitlements'
import { PLAN_LIMITS } from '@/lib/constants'
import { PLAN_SECTION_NAVIGATE_EVENT } from '@/lib/planSectionNavigation'
import { cn, formatCurrency, formatNumber } from '@/lib/utils'
import { planDisplayName } from '@/lib/planDisplayNames'

// One baseline summary per employer-contribution card, so the Plan A reference
// strip on each card describes only that card's source.
function summarizeMatch(c) {
  if (!c.matchEnabled) return 'No match'
  const parts = []
  if (c.matchMode === 'grouped') {
    const groupCount = (c.matchGroups || []).length
    parts.push(`Match by class (${groupCount} class${groupCount === 1 ? '' : 'es'})`)
  } else {
    const tiers = (c.matchTiers || []).map(t => `${t.matchPercent}% up to ${t.upToPercent}%`).join(', ')
    parts.push(`Match ${tiers || 'not set'}`)
  }
  if (c.matchCapPerEmployee > 0) parts.push(`capped at ${formatCurrency(c.matchCapPerEmployee)} per employee`)
  return parts.join(' · ')
}

function summarizeNec(c) {
  if (!c.necEnabled) return 'No nonelective contribution'
  return `${c.necPercent}% ${c.necIsSafeHarbor ? 'safe-harbor ' : ''}nonelective contribution`
}

function summarizeProfitSharing(c) {
  const profitSharing = c.profitSharing
  if (!profitSharing?.enabled) return 'No profit sharing'
  const formula = profitSharing.formula || {}
  if (formula.type === 'new_comparability') {
    const classCount = (formula.groups || []).length
    return `New-comparability profit sharing (${classCount} class rate${classCount === 1 ? '' : 's'})`
  }
  if (formula.type === 'age_weighted') return `Age-weighted profit sharing (${formatCurrency(profitSharing.funding?.amount || 0)} pool)`
  if (formula.type === 'flat_dollar') return `${formatCurrency(formula.amount || 0)} profit sharing per employee`
  if (formula.type === 'integrated') return 'Integrated profit sharing'
  if (formula.type === 'points') return 'Points-based profit sharing'
  return `${formula.percent || 0}% profit sharing`
}

function summarizeAfterTax(a) {
  if (!a || !a.allowed) return 'After-tax contributions not allowed'
  const parts = []
  if (a.maxPercent > 0) parts.push(`up to ${a.maxPercent}% of comp`)
  if (a.maxDollar > 0) parts.push(`up to ${formatCurrency(a.maxDollar)}`)
  return parts.length ? `After-tax allowed - ${parts.join(', ')}` : 'After-tax allowed (415(c) cap only)'
}

function summarizeCashBalance(cb) {
  if (!cb || !cb.enabled) return 'No cash balance plan'
  const groupCount = (cb.credits || []).length
  return `Cash balance - ${groupCount} group${groupCount === 1 ? '' : 's'}`
}

function summarizeEligibility(e) {
  if (!e.enabled) return 'No minimum eligibility requirements'
  const parts = []
  if (e.minAge) parts.push(`age ${e.minAge}`)
  if (e.minServiceMonths) parts.push(`${e.minServiceMonths} ${e.minServiceMonths === 1 ? 'month' : 'months'} of service`)
  if (e.minHours) parts.push(`${formatNumber(e.minHours)} hours`)
  const base = parts.length ? `Minimum ${parts.join(', ')}` : 'Eligibility required, no thresholds set'
  return `${base} · 2025+ LTPT deferrals applied`
}

function describeSchedule(schedule) {
  if (!schedule) return 'immediate'
  if (schedule.type === 'cliff') return `${schedule.years || 0}-year cliff`
  if (schedule.type === 'graded') return 'graded'
  return 'immediate'
}

function summarizeVesting(v, c) {
  if (!v.enabled) return 'Immediate vesting on all sources'
  const parts = []
  if (c.matchEnabled) parts.push(`match ${describeSchedule(v.matchSchedule)}`)
  if (c.necEnabled) parts.push(`nonelective ${c.necIsSafeHarbor ? 'immediate' : describeSchedule(v.necSchedule)}`)
  if (c.profitSharing?.enabled) parts.push(`profit sharing ${describeSchedule(v.profitSharingSchedule)}`)
  return parts.length ? `Vesting - ${parts.join(', ')}` : 'Vesting enabled'
}

function summarizeTermination(t, prorate) {
  const parts = [t.includeInMetrics ? 'Terminated employees included in metrics' : 'Terminated employees excluded from metrics']
  if (t.terminatedEarnContributions) parts.push('they earn employer contributions')
  if (prorate) parts.push('compensation prorated')
  return parts.join(' · ')
}

// The Plan A reference strip: shown above each section's fields while editing
// another plan, so you always see the baseline you are changing against. Plan A
// itself stays fully editable – switch to its chip to change it.
function PlanASummary({ summary }) {
  return (
    <div role="note" className="rounded-md border border-border bg-muted/70 px-3.5 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Current Plan · starting point
      </div>
      <p className="mt-1 text-sm leading-relaxed text-foreground">{summary}</p>
    </div>
  )
}

// Each employer-contribution source is now its own section, so the "same as
// Plan A" tag has to compare that source's own fields rather than the whole
// employerContributions blob.
const matchFacts = (plan) => {
  const c = plan?.employerContributions || {}
  return [c.matchEnabled, c.matchMode, c.matchTiers, c.matchGroups, c.matchCapPerEmployee]
}
const necFacts = (plan) => {
  const c = plan?.employerContributions || {}
  return [c.necEnabled, c.necPercent, c.necIsSafeHarbor]
}

export const PLAN_SECTIONS = [
  { key: 'planYear', label: 'Plan year & identity' },
  { key: 'eligibility', label: 'Eligibility' },
  { key: 'match', label: 'Tiered match' },
  { key: 'nec', label: 'Nonelective contribution' },
  { key: 'profitSharing', label: 'Profit sharing' },
  { key: 'cashBalance', label: 'Cash balance' },
  { key: 'afterTax', label: 'After-tax contributions' },
  { key: 'vesting', label: 'Vesting' },
  { key: 'termination', label: 'Termination handling' },
]

export default function PlanSettings({
  demoMode = false,
  toolbarActions = null,
  planKey = null,
  comparisonColumn = false,
  activeSection: controlledActiveSection = null,
  onSectionChange = null,
  singleSection = false,
  showToolbar = true,
}) {
  const comparing = useStore(isComparing)
  const store = useStore(useShallow((state) => ({
    planA: state.planA,
    planB: state.planB,
    planC: state.planC,
    planD: state.planD,
    employees: state.employees,
    censusMode: state.censusMode,
    censusBaseline: state.censusBaseline,
    analysis: state.analysis,
    employerProfile: state.employerProfile,
    updatePlan: state.updatePlan,
    applyNewCompSolution: state.applyNewCompSolution,
    censusLocal: state.censusLocal,
    setPerPersonAmount: state.setPerPersonAmount,
    setSolverTargets: state.setSolverTargets,
    setWorkspaceStage: state.setWorkspaceStage,
    resetInputValidation: state.resetInputValidation,
    resetPlanToBaseline: state.resetPlanToBaseline,
  })))

  const targetPlanKey = planKey && store[planKey] ? planKey : 'planA'
  const editingAlternative = targetPlanKey !== 'planA'

  // The allocation classes the ACTIVE plan can set rates for: every class code
  // present in the census, plus any class its existing rates still point at so
  // loading a plan against a different census never drops a rate. One list and
  // one head-count, because a person is in exactly one class for every source.
  const activePlanForCounts = store[targetPlanKey]
  const activeClasses = React.useMemo(
    () => deriveAllocationClasses(store.employees, referencedClassesForPlan(activePlanForCounts)),
    [store.employees, activePlanForCounts],
  )
  const activeMemberCounts = React.useMemo(
    () => countClassMembers(store.employees, activeClasses),
    [store.employees, activeClasses],
  )

  const getConfig = ({ key, plan, label }) => {
    const update = updates => store.updatePlan(key, updates)
    const updateContributions = updates => update({ employerContributions: updates })
    const updateEligibility = updates => update({ eligibility: updates })
    const updateVesting = updates => update({ vesting: updates })
    const updateTermination = updates => update({ terminationHandling: updates })
    const updateAfterTax = updates => update({ afterTax: updates })
    const tiers = plan.employerContributions.matchTiers
    const matchGroups = plan.employerContributions.matchGroups || []
    // Apply a solved design and its plan-specific profit-sharing assignments in
    // one store transaction. Match and cash-balance groups continue to use the
    // shared census allocation_group; solver assignments never overwrite that field or
    // leak into another comparison plan.
    const applyNewCompSolution = ({ design, groupAssignments, proposal = null, cashBalance = null }) => {
      if (!groupAssignments) return { applied: false, groupsApplied: false }
      // Demo census limits are checked before either side of the solution is
      // written, so a blocked apply cannot leave the plan and census mismatched.
      if (demoMode && !isDemoCensusAllowed(store.censusMode, store.employees, store.censusBaseline)) {
        return { applied: false, groupsApplied: false, demoBlocked: true }
      }
      const applied = store.applyNewCompSolution(key, design, groupAssignments, proposal, cashBalance)
      return { applied, groupsApplied: applied }
    }


    return {
      key,
      plan,
      label,
      variant: key !== 'planA' ? 'alt' : undefined,
      suffix: comparing && !comparisonColumn ? ` - ${label}` : '',
      applyNewCompSolution,
      contributions: {
        onMatchEnabledChange: value => updateContributions({ matchEnabled: value }),
        onMatchModeChange: value => updateContributions({ matchMode: value }),
        onAddTier: () => {
          const last = tiers[tiers.length - 1]
          updateContributions({
            matchTiers: [
              ...tiers,
              { matchPercent: 25, upToPercent: (last?.upToPercent || 0) + 2 },
            ],
          })
        },
        onUpdateTier: (index, updates) => updateContributions({
          matchTiers: tiers.map((tier, tierIndex) => (
            tierIndex === index ? { ...tier, ...updates } : tier
          )),
        }),
        onDeleteTier: index => updateContributions({
          matchTiers: tiers.filter((_, tierIndex) => tierIndex !== index),
        }),
        onResetTiers: () => {
          updateContributions({
            matchTiers: [
              { matchPercent: 100, upToPercent: 1 },
              { matchPercent: 50, upToPercent: 6 },
            ],
          })
          store.resetInputValidation()
        },
        onMatchCapChange: value => updateContributions({ matchCapPerEmployee: value }),
        onAddMatchGroup: (groupId = '') => updateContributions({
          matchGroups: [
            ...matchGroups,
            createMatchGroup(groupId),
          ],
        }),
        onUpdateMatchGroup: (index, nextGroup) => updateContributions({
          matchGroups: matchGroups.map((entry, entryIndex) => (
            entryIndex === index ? nextGroup : entry
          )),
        }),
        onDeleteMatchGroup: index => updateContributions({
          matchGroups: matchGroups.filter((_, entryIndex) => entryIndex !== index),
        }),
        onNecEnabledChange: value => updateContributions({ necEnabled: value }),
        onNecPercentChange: value => updateContributions({ necPercent: value }),
        onNecSafeHarborChange: value => updateContributions({ necIsSafeHarbor: value }),
        onPsEnabledChange: value => updateContributions({
          profitSharing: { ...plan.employerContributions.profitSharing, enabled: value },
        }),
        onPsConditionsChange: patch => updateContributions({
          profitSharing: {
            ...plan.employerContributions.profitSharing,
            conditions: { ...plan.employerContributions.profitSharing.conditions, ...patch },
          },
        }),
      },
      eligibility: {
        onEnabledChange: value => updateEligibility({ enabled: value }),
        onMinAgeChange: value => updateEligibility({ minAge: value }),
        onMinServiceChange: value => updateEligibility({ minServiceMonths: value }),
        onMinHoursChange: value => updateEligibility({ minHours: value }),
      },
      afterTax: {
        onAllowedChange: value => updateAfterTax({ allowed: value }),
        onMaxPercentChange: value => updateAfterTax({ maxPercent: value }),
        onMaxDollarChange: value => updateAfterTax({ maxDollar: value }),
      },
      vesting: {
        onEnabledChange: value => updateVesting({ enabled: value }),
        onMatchScheduleChange: value => updateVesting({ matchSchedule: value }),
        onNecScheduleChange: value => updateVesting({ necSchedule: value }),
        onPsScheduleChange: value => updateVesting({ profitSharingSchedule: value }),
      },
      termination: {
        onIncludeChange: value => updateTermination({ includeInMetrics: value }),
        onTerminatedEarnChange: value => updateTermination({ terminatedEarnContributions: value }),
        onProrateChange: value => update({ prorateCompensation: value }),
        onShortTermThresholdChange: value => updateTermination({ shortTermThresholdDays: value }),
      },
      onPlanYearChange: value => {
        update({ planYear: parseInt(value, 10) })
        toast.success(`${label} plan year updated`, {
          description: `${value} limits are now applied.`,
        })
      },
    }
  }

  // A standalone editor is Plan A. Comparison columns pass an explicit key so
  // every visible plan remains independently editable at the same time.
  const activePlan = store[targetPlanKey]
  const activePlanName = planDisplayName(targetPlanKey, activePlan.name)
  const config = getConfig({ key: targetPlanKey, plan: activePlan, label: activePlanName })
  const planA = store.planA
  // Keep the established anchors in the normal editor. Comparison columns need
  // plan-prefixed ids because several copies of the same card are mounted.
  const sectionId = (suffix) => comparisonColumn ? `${targetPlanKey}-${suffix}` : suffix
  const matchesBaseline = !editingAlternative || arePlansEquivalent(planA, activePlan)

  // A section's title gets a muted "same" tag when an alternative matches Plan A,
  // mirroring the outputs comparison which reads "same" wherever a plan equals A.
  const sameBySection = editingAlternative ? {
    match: JSON.stringify(matchFacts(planA)) === JSON.stringify(matchFacts(activePlan)),
    nec: JSON.stringify(necFacts(planA)) === JSON.stringify(necFacts(activePlan)),
    profitSharing: JSON.stringify(planA.employerContributions?.profitSharing) === JSON.stringify(activePlan.employerContributions?.profitSharing),
    cashBalance: JSON.stringify(planA.cashBalance) === JSON.stringify(activePlan.cashBalance),
    afterTax: JSON.stringify(planA.afterTax) === JSON.stringify(activePlan.afterTax),
    eligibility: JSON.stringify(planA.eligibility) === JSON.stringify(activePlan.eligibility),
    vesting: JSON.stringify(planA.vesting) === JSON.stringify(activePlan.vesting),
    termination: JSON.stringify([planA.terminationHandling, planA.prorateCompensation]) === JSON.stringify([activePlan.terminationHandling, activePlan.prorateCompensation]),
    planYear: planA.planYear === activePlan.planYear,
  } : {}

  const titleFor = (base, section) => {
    const text = `${base}${config.suffix}`
    if (!editingAlternative || !sameBySection[section]) return text
    return (
      <span className="inline-flex items-baseline gap-2">
        {text}
        <span className="text-xs font-medium text-muted-foreground">same</span>
      </span>
    )
  }

  const [internalActiveSection, setInternalActiveSection] = React.useState('match')
  const activeSection = controlledActiveSection || internalActiveSection
  const detailRef = React.useRef(null)
  const programmaticScrollRef = React.useRef(false)

  // The pane lives in a resizable split, so its width is independent of the
  // viewport – measure the container itself to decide between the side rail and
  // the stacked dropdown rather than a viewport media query.
  const containerRef = React.useRef(null)
  const [isNarrow, setIsNarrow] = React.useState(false)
  React.useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width < 640)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // The regular editor keeps every section mounted and scrolls to the selected
  // one. Comparison columns mount only the shared selected section.
  // Jumps instantly rather than smoothly on purpose: something in this pane
  // aborts an in-flight smooth scroll after a few pixels (reproducible straight
  // from the console, so it is not the scroll-spy re-render), which left the
  // rail highlighting a section the pane never moved to. An instant jump lands
  // every time. The spy is suppressed across the jump so it cannot immediately
  // re-derive a different active section from the intermediate scroll event.
  const selectSection = React.useCallback((key) => {
    setInternalActiveSection(key)
    onSectionChange?.(key)
    if (singleSection) return
    requestAnimationFrame(() => {
      const container = detailRef.current
      const target = container?.querySelector(`[data-plan-section="${key}"]`)
      if (!container || !target) return
      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
      programmaticScrollRef.current = true
      container.scrollTop = Math.max(0, top - 8)
    })
  }, [onSectionChange, singleSection])

  React.useEffect(() => {
    const handleNavigate = (event) => {
      const section = event.detail?.section
      if (PLAN_SECTIONS.some((candidate) => candidate.key === section)) selectSection(section)
    }
    window.addEventListener(PLAN_SECTION_NAVIGATE_EVENT, handleNavigate)
    return () => window.removeEventListener(PLAN_SECTION_NAVIGATE_EVENT, handleNavigate)
  }, [selectSection])

  const sectionCards = {
    match: (
      <MatchCard
        id={sectionId('plan-match')}
        title={titleFor('Tiered match', 'match')}
        description="Match employee deferrals using a standard or allocation-class formula."
        contributions={config.plan.employerContributions}
        reference={editingAlternative && !comparisonColumn ? <PlanASummary summary={summarizeMatch(planA.employerContributions)} /> : null}
        compareTo={editingAlternative ? planA.employerContributions : null}
        allocationGroups={activeClasses}
        matchMemberCounts={activeMemberCounts}
        {...config.contributions}
        variant={config.variant}
      />
    ),
    nec: (
      <NecCard
        id={sectionId('plan-nec')}
        title={titleFor('Nonelective contribution', 'nec')}
        description="Contribute a fixed percentage of compensation regardless of employee deferrals."
        contributions={config.plan.employerContributions}
        reference={editingAlternative && !comparisonColumn ? <PlanASummary summary={summarizeNec(planA.employerContributions)} /> : null}
        compareTo={editingAlternative ? planA.employerContributions : null}
        {...config.contributions}
        variant={config.variant}
      />
    ),
    profitSharing: (
      <ProfitSharingCard
        id={sectionId('plan-profit-sharing')}
        title={titleFor('Profit sharing', 'profitSharing')}
        description="A discretionary contribution, allocated by the formula you choose."
        contributions={config.plan.employerContributions}
        reference={editingAlternative && !comparisonColumn ? <PlanASummary summary={summarizeProfitSharing(planA.employerContributions)} /> : null}
        designer={(
          <ProfitSharingSection
            idPrefix={comparisonColumn ? `${targetPlanKey}-plan-ps` : 'plan-ps'}
            design={selectProfitSharingDesign(config.plan)}
            plan={config.plan}
            employerProfile={store.employerProfile}
            metrics={store.analysis?.[config.key]?.metrics || null}
            allocationGroups={activeClasses}
            memberCounts={activeMemberCounts}
            allocationDiagnostics={store.analysis?.[config.key]?.allocationDiagnostics || []}
            crossTesting={store.analysis?.[config.key]?.metrics?.crossTesting || null}
            employees={store.employees}
            calculatedEmployees={config.key === 'planA' ? (store.analysis?.planA?.calculatedEmployees || []) : []}
            perPersonAmounts={store.censusLocal?.byPlanKey?.[config.key]?.perPersonAmounts?.profitSharing || {}}
            solverTargets={store.censusLocal?.byPlanKey?.[config.key]?.solverTargets?.profitSharing || []}
            onUpdateDesign={(next) => store.updatePlan(config.key, { employerContributions: { profitSharing: next } })}
            onManageGroups={() => store.setWorkspaceStage('people')}
            onSetPerPersonAmount={(employeeId, amount) => store.setPerPersonAmount(config.key, 'profitSharing', employeeId, amount)}
            onSetSolverTargets={(ids) => store.setSolverTargets(config.key, 'profitSharing', ids)}
            onApplySolution={config.applyNewCompSolution}
          />
        )}
        {...config.contributions}
        variant={config.variant}
      />
    ),
    cashBalance: (
      <CashBalanceStructuredCard
        id={sectionId('plan-cash-balance')}
        title={titleFor('Cash balance', 'cashBalance')}
        description="Explicit annual benefit credits and a fixed-interest directional projection."
        reference={editingAlternative && !comparisonColumn ? <PlanASummary summary={summarizeCashBalance(planA.cashBalance)} /> : null}
        planYear={config.plan.planYear}
        design={config.plan.cashBalance}
        allocationGroups={activeClasses}
        memberCounts={activeMemberCounts}
        onUpdateDesign={(next) => store.updatePlan(config.key, { cashBalance: next })}
      />
    ),
    afterTax: (
      <AfterTaxCard
        id={sectionId('plan-after-tax')}
        title={titleFor('After-tax contributions', 'afterTax')}
        description="Employee after-tax (non-Roth) contributions that fill the 415(c) cap above deferrals and employer money. Converting them to Roth is the mega-backdoor Roth path. This tool models the contribution, not the conversion."
        reference={editingAlternative && !comparisonColumn ? <PlanASummary summary={summarizeAfterTax(planA.afterTax)} /> : null}
        compareTo={editingAlternative ?planA.afterTax : null}
        afterTax={config.plan.afterTax}
        {...config.afterTax}
        variant={config.variant}
      />
    ),
    eligibility: (
      <EligibilityCard
        id={sectionId('plan-eligibility-rules')}
        title={titleFor('Minimum eligibility', 'eligibility')}
        description="These requirements apply uniformly to match, NEC, and profit sharing. Missing required data is shown as unknown and contributions remain modeled."
        reference={editingAlternative && !comparisonColumn ? <PlanASummary summary={summarizeEligibility(planA.eligibility)} /> : null}
        compareTo={editingAlternative ?planA.eligibility : null}
        enabled={config.plan.eligibility.enabled}
        minAge={config.plan.eligibility.minAge}
        minServiceMonths={config.plan.eligibility.minServiceMonths}
        minHours={config.plan.eligibility.minHours}
        planYear={config.plan.planYear}
        {...config.eligibility}
        variant={config.variant}
      />
    ),
    vesting: (
      <VestingCard
        id={sectionId('plan-vesting-schedule')}
        title={titleFor('Vesting', 'vesting')}
        description="Configure vesting for employer sources. Service-dependent sources remain unresolved when service is unknown; immediate and safe-harbor sources remain known."
        reference={editingAlternative && !comparisonColumn ? <PlanASummary summary={summarizeVesting(planA.vesting, planA.employerContributions)} /> : null}
        vesting={config.plan.vesting}
        matchEnabled={config.plan.employerContributions.matchEnabled}
        necEnabled={config.plan.employerContributions.necEnabled}
        necIsSafeHarbor={config.plan.employerContributions.necIsSafeHarbor}
        profitSharingEnabled={isProfitSharingEnabled(config.plan)}
        {...config.vesting}
        variant={config.variant}
      />
    ),
    termination: (
      <TerminationCard
        id={sectionId('plan-termination-handling')}
        title={titleFor('Termination handling', 'termination')}
        description="Status controls termination treatment. Dates provide valuation and proration timing only for records marked Terminated."
        reference={editingAlternative && !comparisonColumn ? <PlanASummary summary={summarizeTermination(planA.terminationHandling, planA.prorateCompensation)} /> : null}
        includeInMetrics={config.plan.terminationHandling.includeInMetrics}
        terminatedEarnContributions={config.plan.terminationHandling.terminatedEarnContributions}
        prorateCompensation={config.plan.prorateCompensation}
        shortTermThresholdDays={config.plan.terminationHandling.shortTermThresholdDays}
        {...config.termination}
        variant={config.variant}
      />
    ),
    planYear: (
      <PlanYearCard
        id={sectionId('plan-year-limits')}
        title={titleFor('Plan year & limits', 'planYear')}
        reference={editingAlternative && !comparisonColumn ? <PlanASummary summary={`${planA.planYear} plan year and limits`} /> : null}
        planYear={config.plan.planYear}
        onPlanYearChange={config.onPlanYearChange}
        limits={PLAN_LIMITS[config.plan.planYear]}
        variant={config.variant}
      />
    ),
  }

  // The card fills the pane and stays put; only the detail area inside it
  // scrolls, so the padding around the card is constant. The rail/dropdown sit
  // outside the scroll region – forward wheel input over them to the detail so
  // you can scroll from anywhere on the pane.
  const handleRailWheel = (event) => {
    const el = detailRef.current
    if (!el || el.scrollHeight <= el.clientHeight) return
    el.scrollTop += event.deltaY
    event.preventDefault()
  }

  // Scroll-spy: with every section stacked in one scrollable column, the
  // rail's highlighted section should track whatever has scrolled to the top
  // of the pane, the same way the outputs pane's nav follows its scroll.
  React.useEffect(() => {
    if (singleSection) return
    const container = detailRef.current
    if (!container) return

    const sections = PLAN_SECTIONS
      .map((section) => ({ key: section.key, element: container.querySelector(`[data-plan-section="${section.key}"]`) }))
      .filter((section) => section.element)
    if (sections.length === 0) return

    const getTop = (element) => (
      element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
    )

    const updateActiveSection = () => {
      const activeLine = container.scrollTop + 24
      let next = sections[0].key
      for (const section of sections) {
        if (getTop(section.element) <= activeLine) next = section.key
      }
      setInternalActiveSection((prev) => (prev === next ? prev : next))
    }

    let frame = null
    let settleTimer = null
    const onScroll = () => {
      // A rail click drives its own scroll and has already set the active
      // section. Re-rendering mid-animation would cancel it, so wait until the
      // scroll stops arriving before resuming the spy.
      if (programmaticScrollRef.current) {
        window.clearTimeout(settleTimer)
        settleTimer = window.setTimeout(() => { programmaticScrollRef.current = false }, 120)
        return
      }
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = null
        updateActiveSection()
      })
    }

    updateActiveSection()
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
      window.clearTimeout(settleTimer)
    }
  }, [isNarrow, singleSection])

  // All sections render together in one stacked, scrollable column; the rail
  // just scrolls to a section rather than swapping which one is mounted.
  const detail = singleSection ? (
    <div data-plan-section={activeSection}>{sectionCards[activeSection]}</div>
  ) : (
    <div className="flex min-w-0 flex-col gap-3">
      {PLAN_SECTIONS.map((section) => (
        <div key={section.key} data-plan-section={section.key}>
          {sectionCards[section.key]}
        </div>
      ))}
    </div>
  )

  return (
    <div id={`plan-settings-${targetPlanKey}`} data-plan-key={targetPlanKey} className="flex h-full min-h-0 flex-col">
      {showToolbar && <div className="mx-auto mb-1.5 w-full max-w-[1200px] shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="ml-auto flex items-center gap-2">
            {toolbarActions}
            {editingAlternative && (
              <button
                type="button"
                disabled={matchesBaseline}
                onClick={() => {
                  store.resetPlanToBaseline(targetPlanKey)
                  toast.success(`${activePlanName} restored from Current Plan`)
                }}
                title={matchesBaseline
                  ? `${activePlanName} already matches Current Plan`
                  : `Discard ${activePlanName}'s edits and restore it from Current Plan`}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RotateCcw className="h-3 w-3" aria-hidden="true" />
                Restore {activePlanName}
              </button>
            )}
          </div>
        </div>
        {editingAlternative && matchesBaseline && (
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            {activePlanName} currently matches Current Plan. Edit any field below to model a change. Outputs update live.
          </p>
        )}
      </div>}
      {/* Master/detail: a rail (or a dropdown on a narrow pane) picks one section;
          only that section renders. The card fills the pane and stays put; the
          detail area inside it is the only thing that scrolls, so the padding
          around the card is constant while scrolling. */}
      <div ref={containerRef} className="mx-auto flex min-h-0 w-full max-w-[1200px] flex-1 flex-col">
        {singleSection ? (
          <div ref={detailRef} data-plan-detail className="min-h-0 min-w-0 flex-1 overflow-y-auto bg-cat-plan-soft p-1.5">
            {detail}
          </div>
        ) : isNarrow ? (
          <div className="flex max-h-full min-h-0 flex-col bg-cat-plan-soft ring-1 ring-foreground/10">
            <div className="shrink-0 border-b border-foreground/10 p-2" onWheel={handleRailWheel}>
              <Select value={activeSection} onValueChange={selectSection}>
                <SelectTrigger className="w-full" aria-label="Plan settings section">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_SECTIONS.map((section) => (
                    <SelectItem key={section.key} value={section.key}>{section.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div ref={detailRef} data-plan-detail className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto p-1.5">{detail}</div>
          </div>
        ) : (
          <div className="flex max-h-full min-h-0 items-stretch bg-cat-plan-soft ring-1 ring-foreground/10">
            <div className="shrink-0 border-r border-foreground/10" onWheel={handleRailWheel}>
              <nav className="flex w-48 flex-col gap-0.5 p-1.5" aria-label="Plan settings sections">
                {PLAN_SECTIONS.map((section) => {
                  const isActive = section.key === activeSection
                  return (
                    <button
                      key={section.key}
                      type="button"
                      onClick={() => selectSection(section.key)}
                      aria-current={isActive || undefined}
                      className={cn(
                        'rounded-md px-2.5 py-1.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                        isActive
                          ? 'bg-cat-plan/15 font-medium text-foreground'
                          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                      )}
                    >
                      {section.label}
                    </button>
                  )
                })}
              </nav>
            </div>
            <div ref={detailRef} data-plan-detail className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto p-1.5">{detail}</div>
          </div>
        )}
      </div>
    </div>
  )
}
